/**
 * Add / Edit a Material Library item.
 *
 * Mirrors mobile's add-material flow. Fields are driven by the
 * selected category (different categories show different prompts —
 * e.g. plywood asks Thickness as chips, hardware asks Type as
 * chips). Common to all: name, unit, default rate.
 *
 * Writes to `materialLibrary/{id}` via `createLibraryItem` /
 * `updateLibraryItem` (same docs mobile writes).
 */
import { useEffect, useState } from 'react';

import { canManageProjects } from '@shared/perms';

import { useAuth } from '@/auth/AuthProvider';
import {
  MATERIAL_CATEGORIES,
  UNIT_OPTIONS,
  createLibraryItem,
  deleteLibraryItem,
  getCategoryConfig,
  updateLibraryItem,
  type MaterialCategory,
  type MaterialLibraryItem,
} from '@/lib/materialLibrary';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: '#fff',
  fontSize: 13,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
};
const LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-tertiary)',
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
};

export function MaterialLibraryFormSlideOver({
  open,
  onClose,
  orgId,
  item,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  item: MaterialLibraryItem | null;
  onSaved?: (id: string) => void;
}) {
  const { user } = useAuth();
  const { role: orgRole } = useCurrentOrgRole();
  const canWrite = canManageProjects(orgRole);
  const isEdit = Boolean(item);

  const [category, setCategory] = useState<MaterialCategory>('plywood');
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [variety, setVariety] = useState('');
  const [make, setMake] = useState('');
  const [size, setSize] = useState('');
  const [unit, setUnit] = useState('');
  const [rate, setRate] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (item) {
      setCategory(item.category);
      setName(item.name);
      setBrand(item.brand);
      setVariety(item.variety);
      setMake(item.make);
      setSize(item.size);
      setUnit(item.unit);
      setRate(typeof item.defaultRate === 'number' ? String(item.defaultRate) : '');
    } else {
      const c = getCategoryConfig('plywood');
      setCategory('plywood');
      setName('');
      setBrand('');
      setVariety('');
      setMake('');
      setSize('');
      setUnit(c.defaultUnit);
      setRate('');
    }
    setConfirmDelete(false);
    setError(null);
  }, [open, item]);

  // When category changes (in create mode), refresh suggested unit.
  useEffect(() => {
    if (item) return;
    const c = getCategoryConfig(category);
    setUnit(c.defaultUnit);
    // Reset chip-driven fields so stale picks don't carry over.
    setVariety('');
    setMake('');
    setSize('');
  }, [category, item]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, busy, onClose]);

  const cfg = getCategoryConfig(category);

  const handleSubmit = async () => {
    setError(null);
    if (!orgId || !user) return setError('Sign-in required.');
    if (name.trim().length < 2)
      return setError('Name must be at least 2 characters.');
    const rateNum = rate ? Number(rate.replace(/,/g, '')) : undefined;
    if (rateNum !== undefined && (Number.isNaN(rateNum) || rateNum < 0))
      return setError('Default rate must be a non-negative number.');
    setBusy(true);
    try {
      let id: string;
      if (isEdit && item) {
        await updateLibraryItem(item.id, {
          category,
          name: name.trim(),
          brand: brand.trim(),
          variety: variety.trim(),
          make: make.trim(),
          size: size.trim(),
          unit: unit.trim(),
          defaultRate: rateNum,
        });
        id = item.id;
      } else {
        id = await createLibraryItem({
          orgId,
          category,
          name: name.trim(),
          brand: brand.trim(),
          variety: variety.trim(),
          make: make.trim(),
          size: size.trim(),
          unit: unit.trim() || cfg.defaultUnit,
          defaultRate: rateNum,
          createdBy: user.uid,
        });
      }
      onSaved?.(id);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!item) return;
    setBusy(true);
    try {
      await deleteLibraryItem(item.id);
      onClose();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  // Render a category-defined field. `chips` types render as a
  // picker; `text` as an input.
  const renderField = (
    field: (typeof cfg.fields)[number],
    value: string,
    setValue: (v: string) => void,
  ) => {
    if (field.type === 'chips' && field.options?.length) {
      return (
        <div>
          <label style={LABEL}>{field.label}</label>
          <div
            style={{
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
            }}
          >
            {field.options.map((o) => {
              const active = o === value;
              return (
                <button
                  key={o}
                  type="button"
                  onClick={() => setValue(active ? '' : o)}
                  style={{
                    padding: '6px 10px',
                    border: active
                      ? '1.5px solid var(--brand)'
                      : '1px solid var(--border)',
                    background: active ? 'var(--brand-bg)' : '#fff',
                    color: active ? 'var(--brand)' : 'var(--text-primary)',
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: active ? 700 : 500,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {o}
                </button>
              );
            })}
          </div>
        </div>
      );
    }
    return (
      <div>
        <label style={LABEL}>{field.label}</label>
        <input
          style={INPUT}
          value={value}
          onChange={(e) => setValue(e.currentTarget.value)}
          placeholder={field.placeholder || ''}
        />
      </div>
    );
  };

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={() => !busy && onClose()}
      />
      <div className={`slide-over${open ? ' open' : ''}`}>
        <div className="slide-header">
          <h2 className="slide-title">
            <i className="ph ph-package" />{' '}
            {isEdit ? 'Edit Material' : 'New Material'}
          </h2>
          <button
            type="button"
            className="slide-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {error ? (
            <div
              style={{
                background: 'var(--critical-bg)',
                color: 'var(--critical-text)',
                padding: 12,
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          ) : null}

          {/* Category picker — grid of tinted chips */}
          <div>
            <label style={LABEL}>Category *</label>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                gap: 6,
              }}
            >
              {MATERIAL_CATEGORIES.map((c) => {
                const active = c.key === category;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setCategory(c.key)}
                    style={{
                      padding: '8px 10px',
                      border: active
                        ? `1.5px solid ${c.color}`
                        : '1px solid var(--border)',
                      background: active ? c.tintBg : '#fff',
                      color: active ? c.color : 'var(--text-primary)',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: active ? 700 : 500,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      textAlign: 'left',
                    }}
                  >
                    <i className={c.icon} />
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Display name — separate from variety/brand */}
          <div>
            <label style={LABEL}>Display name *</label>
            <input
              style={INPUT}
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              placeholder={`e.g. ${cfg.label} — ${cfg.fields[0]?.label ?? 'Spec'}`}
              autoFocus
            />
          </div>

          {/* Category-driven fields */}
          {cfg.fields.map((field) => {
            const value =
              field.key === 'brand'
                ? brand
                : field.key === 'variety'
                  ? variety
                  : field.key === 'make'
                    ? make
                    : field.key === 'size'
                      ? size
                      : '';
            const setValue =
              field.key === 'brand'
                ? setBrand
                : field.key === 'variety'
                  ? setVariety
                  : field.key === 'make'
                    ? setMake
                    : field.key === 'size'
                      ? setSize
                      : () => {};
            return (
              <div key={field.key}>{renderField(field, value, setValue)}</div>
            );
          })}

          {/* Unit + default rate */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={LABEL}>Unit *</label>
              <select
                style={{ ...INPUT, height: 38 }}
                value={unit}
                onChange={(e) => setUnit(e.currentTarget.value)}
              >
                {UNIT_OPTIONS.map((u) => (
                  <option key={u.key} value={u.key}>
                    {u.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={LABEL}>Default rate (₹)</label>
              <input
                style={INPUT}
                value={rate}
                onChange={(e) =>
                  setRate(e.currentTarget.value.replace(/[^\d.]/g, ''))
                }
                placeholder="Optional"
                inputMode="decimal"
              />
            </div>
          </div>

          {/* Footer actions */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 8,
              paddingTop: 16,
              borderTop: '1px solid var(--border)',
            }}
          >
            {isEdit ? (
              confirmDelete ? (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--critical)' }}>
                    Delete from library?
                  </span>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setConfirmDelete(false)}
                    disabled={busy}
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleDelete}
                    disabled={busy}
                    style={{
                      background: 'var(--critical)',
                      borderColor: 'var(--critical)',
                    }}
                  >
                    Yes, delete
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setConfirmDelete(true)}
                  disabled={busy}
                  style={{ color: 'var(--critical)' }}
                >
                  <i className="ph ph-trash" /> Delete
                </button>
              )
            ) : (
              <div />
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {!canWrite ? (
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--text-tertiary)',
                    fontStyle: 'italic',
                  }}
                >
                  Read-only
                </span>
              ) : null}
              <button
                type="button"
                className="btn-secondary"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSubmit}
                disabled={busy || !canWrite}
                title={!canWrite ? 'Your role cannot edit material library' : undefined}
              >
                {busy
                  ? 'Saving…'
                  : isEdit
                    ? 'Save changes'
                    : 'Add to library'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
